import { usePopup } from '../../../context/PopupProvider';
import { useCallback, useEffect, useState } from 'react';
import { showAlert } from '@silverfort/sf-components';
import { ENROLLMENT_EMAIL_REQUEST_DEFAULT_FORM_VALUES } from './useSendEnrollmentRequestForm';
import { RecipientType, SendEnrollmentEmailPopupState } from '../types';
import PreviewEmailContent from '../PreviewEmailPopup/PreviewEmailContent';
import { Entity, UserSession } from '../../../../../api';
import { EnrollmentEmailFormValues } from '../../../types/forms';
import useSendEnrollmentEmail, {
    RecipientDetails,
    SendEnrollmentEmailDetails
} from '../../../api/mutations/useSendEnrollmentEmail';
import CustomizeEnrollmentEmailContent from '../CustomizeEmailPopup/CustomizeEnrollmentEmailContent';
import { PreviewEmailFooter } from '../PreviewEmailPopup/PreviewEmailFooter';
import CustomizeEnrollmentEmailFooter from '../CustomizeEmailPopup/CustomizeEnrollmentEmailFooter';
import { SystemData } from '../../../types/SystemData';
import { useGetEnrollmentCompanyLogoUrl } from '../../../api';
import EntitiesSearchPopupContent from '../EntitiesSearchPopup/EntitiesSearchPopupContent';
import EntitiesSearchFooter from '../EntitiesSearchPopup/EntitiesSearchPopupFooter';

interface UseSendEnrollmentRequestPopupParams {
    recipients?: RecipientDetails[];
    systemData?: SystemData;
    userSession?: UserSession;
}

interface ReturnType {
    openSendEnrollmentRequestPopup: () => void;
}

const convertFormValuesToSendEnrollmentEmailDetails = (formData: EnrollmentEmailFormValues) => {
    return {
        subject: formData.subject,
        content: formData.content,
        tokenType: formData.tokenType,
        tokenTypeId: formData.tokenTypeId!,
        isCustomEmail: formData.isCustomEmail ?? false,
        companyLogoUrl: formData.companyLogoUrl || ''
    };
};

const POPUP_HEADER_DEFAULT_CLASS_NAME = 'h-[64px] pr-6';

const useSendEnrollmentRequestPopup = ({
    recipients = [],
    systemData,
    userSession
}: UseSendEnrollmentRequestPopupParams): ReturnType => {
    const { openPopup, updatePopup } = usePopup();
    const { companyLogoUrl, fetchData: loadCompanyLogoUrl } = useGetEnrollmentCompanyLogoUrl();

    const [entities, setEntities] = useState<Entity[]>([]);

    const [formValues, setFormValues] = useState<EnrollmentEmailFormValues>(
        ENROLLMENT_EMAIL_REQUEST_DEFAULT_FORM_VALUES
    );

    const onSuccess = useCallback(() => {
        showAlert('Enrollment email sent successfully', 'success', { title: 'Enrollment email sent' });
        loadCompanyLogoUrl();
    }, [loadCompanyLogoUrl]);

    const onError = useCallback(() => {
        showAlert('An error occurred while sending the enrollment email', 'error', {
            title: 'Failed to send enrollment email'
        });
    }, []);

    const { sendEnrollmentEmail } = useSendEnrollmentEmail({
        onSuccess,
        onError
    });

    const isOfflineMode = systemData?.Settings?.OfflineMode ?? false;

    const handleRecipientTypeChange = (
        selectedType: RecipientType,
        selectedEntities: Entity[],
        formValues: EnrollmentEmailFormValues
    ) => {
        if (selectedType === 'groupOU') {
            handleOpenEntitiesSelectionPopup(selectedEntities, formValues);
        }
    };

    const handleSendEnrollmentEmail = async (entities: Entity[], formValues: EnrollmentEmailFormValues) => {
        const baseDetails = convertFormValuesToSendEnrollmentEmailDetails(formValues);

        const emailDetails: SendEnrollmentEmailDetails = {
            ...baseDetails,
            recipients: { recipientType: 'groupOU', recipientsDetails: [] }
        };

        if (recipients.length > 0) {
            // recipients are either passed from a parent or are ignored
            emailDetails.recipients.recipientsDetails = recipients;
        } else {
            // If they are not passed we will use entities instead
            emailDetails.recipients.recipientType = formValues.recipientType;
            emailDetails.recipients.recipientsDetails = entities.map((entity) => ({
                guid: entity.guid,
                type: entity.type
            }));
        }

        await sendEnrollmentEmail(emailDetails);
        setFormValues(ENROLLMENT_EMAIL_REQUEST_DEFAULT_FORM_VALUES);
    };

    useEffect(() => {
        setFormValues({ ...formValues, companyLogoUrl: companyLogoUrl });
    }, [companyLogoUrl]);

    const openCustomizeEnrollmentEmailPopup = (
        selectedEntities: Entity[] = entities,
        configuredFormValues: EnrollmentEmailFormValues = formValues,
        shouldUpdate = false
    ) => {
        const config = {
            title: 'Customize enrollment email',
            popupClassName: 'w-[720px] max-w-[720px] max-h-[620px]',
            contentClassName: '',
            ContentComponent: CustomizeEnrollmentEmailContent,
            ContentComponentProps: {
                onPreviewButtonClick: handleOpenPreviewEmailPopup,
                onRecipientTypeChange: handleRecipientTypeChange,
                isOfflineMode,
                isRecipientSectionVisible: recipients.length === 0,
                userSession
            },
            FooterComponent: CustomizeEnrollmentEmailFooter,
            initialState: {
                selectedEntities,
                formValues: configuredFormValues,
                isValid: true
            },
            headerClassName: POPUP_HEADER_DEFAULT_CLASS_NAME,
            FooterComponentProps: {
                onDone: (state: SendEnrollmentEmailPopupState) => {
                    if (!state) {
                        return;
                    }
                    handleSendEnrollmentEmail(state.selectedEntities, state.formValues);
                    setEntities([]);
                },
                onAbort: () => {
                    setEntities([]);
                    setFormValues(ENROLLMENT_EMAIL_REQUEST_DEFAULT_FORM_VALUES);
                }
            }
        };

        if (shouldUpdate) {
            updatePopup(config);
        } else {
            openPopup(config);
        }
    };

    const handleOpenSendEnrollmentRequestPopup = () => {
        openCustomizeEnrollmentEmailPopup();
    };

    const handleCloseEntitiesSelectionPopup = (selectedEntities: Entity[], formValues: EnrollmentEmailFormValues) => {
        setEntities(selectedEntities);
        openCustomizeEnrollmentEmailPopup(selectedEntities, formValues, true);
    };

    const handleOpenEntitiesSelectionPopup = (selectedEntities: Entity[], formValues: EnrollmentEmailFormValues) => {
        updatePopup({
            title: 'Add Users',
            popupClassName: 'w-[544px] h-[504px]',
            contentClassName: 'py-0 px-2',
            FooterComponent: EntitiesSearchFooter,
            FooterComponentProps: {
                onDone: (state) => {
                    if (!state) {
                        return;
                    }
                    handleCloseEntitiesSelectionPopup(state.selectedEntities, formValues);
                },
                onAbort: () => handleCloseEntitiesSelectionPopup(selectedEntities, formValues)
            },
            ContentComponent: EntitiesSearchPopupContent,
            initialState: {
                selectedEntities: selectedEntities,
                formValues: formValues
            },
            headerClassName: POPUP_HEADER_DEFAULT_CLASS_NAME,

            onApprove: () => handleCloseEntitiesSelectionPopup(selectedEntities, formValues)
        });
    };

    const handleOpenPreviewEmailPopup = (
        selectedEntities: Entity[],
        formValues: EnrollmentEmailFormValues,
        isValid: boolean
    ) => {
        updatePopup({
            title: 'Preview enrollment email',
            popupClassName: '!w-[720px] !max-w-[720px] min-h-[417px] max-h-[600px] !overflow-y-auto !overflow-x-hidden',
            ContentComponent: PreviewEmailContent,
            ContentComponentProps: {
                from: 'Silverfort'
            },
            initialState: {
                selectedEntities,
                formValues,
                isValid: isValid
            },
            headerClassName: POPUP_HEADER_DEFAULT_CLASS_NAME,
            FooterComponent: PreviewEmailFooter,
            FooterComponentProps: {
                onDone: (state) => {
                    if (!state) {
                        return;
                    }
                    handleCloseEntitiesSelectionPopup(selectedEntities, formValues);
                },
                onAbort: () => handleCloseEntitiesSelectionPopup(selectedEntities, formValues)
            }
        });
    };

    return { openSendEnrollmentRequestPopup: handleOpenSendEnrollmentRequestPopup };
};

export default useSendEnrollmentRequestPopup;
